/**
 * 📊 MACROS INSIGHTS — /macros/insights
 *
 * Iter. 406.fr.40 — Yuri feature: graphical macro consumption chart
 * (Calories + Protéines + Glucides + Lipides) with a Glow aesthetic across
 * week / month / year windows. Hits GET /api/macros/insights.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Flame, Beef, Wheat, Droplet, Sparkles, Loader2, Brain,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { api, useLanguage } from '../context/AppContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';

const PERIOD_TABS = [
  { key: 'week',  fr: 'Semaine',  en: 'Week' },
  { key: 'month', fr: 'Mois',     en: 'Month' },
  { key: 'year',  fr: 'Année',    en: 'Year' },
];

const METRICS = {
  calories:  { color: '#f97316', glow: 'rgba(249,115,22,0.55)',  label_fr: 'Calories',   label_en: 'Calories',   unit: 'kcal', icon: Flame,   testId: 'macro-calories' },
  protein_g: { color: '#a855f7', glow: 'rgba(168,85,247,0.55)',  label_fr: 'Protéines',  label_en: 'Protein',    unit: 'g',    icon: Beef,    testId: 'macro-protein' },
  carbs_g:   { color: '#06b6d4', glow: 'rgba(6,182,212,0.55)',   label_fr: 'Glucides',   label_en: 'Carbs',      unit: 'g',    icon: Wheat,   testId: 'macro-carbs'   },
  fats_g:    { color: '#eab308', glow: 'rgba(234,179,8,0.55)',   label_fr: 'Lipides',    label_en: 'Fats',       unit: 'g',    icon: Droplet, testId: 'macro-fats'    },
};

const GlowTooltip = ({ active, payload, label, fr }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-zinc-950/95 border border-orange-500/30 rounded-lg px-3 py-2 shadow-[0_0_16px_rgba(249,115,22,0.35)] text-xs backdrop-blur-md">
      <p className="text-[10px] font-bold uppercase tracking-widest text-orange-300 mb-1">{label}</p>
      {payload.map((p) => {
        const m = METRICS[p.dataKey];
        if (!m) return null;
        return (
          <div key={p.dataKey} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: m.color, boxShadow: `0 0 8px ${m.glow}` }} />
              <span className="text-muted-foreground">{fr ? m.label_fr : m.label_en}</span>
            </span>
            <span className="font-black tabular-nums text-foreground">{Math.round(p.value)}<span className="text-[10px] text-muted-foreground ml-1">{m.unit}</span></span>
          </div>
        );
      })}
    </div>
  );
};

const MacroCard = ({ metricKey, avg, target, fr }) => {
  const m = METRICS[metricKey];
  const Icon = m.icon;
  const pct = target > 0 ? Math.min(999, Math.round((avg / target) * 100)) : 0;
  const okColor = pct >= 90 && pct <= 110 ? 'text-emerald-300' : pct < 90 ? 'text-amber-300' : 'text-rose-300';
  return (
    <div
      className="p-3 rounded-xl bg-card/70 ring-1 ring-border/60 hover:ring-orange-500/30 transition-all"
      style={{ boxShadow: `inset 0 0 24px ${m.glow.replace('0.55','0.08')}` }}
      data-testid={`${m.testId}-avg-card`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: m.glow.replace('0.55','0.15') }}>
          <Icon className="w-3.5 h-3.5" style={{ color: m.color }} />
        </span>
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          {fr ? m.label_fr : m.label_en}
        </span>
      </div>
      <p className="font-heading text-xl tabular-nums text-foreground leading-none">
        {Math.round(avg)}<span className="text-xs text-muted-foreground ml-1">{m.unit}</span>
      </p>
      <p className="text-[10px] text-muted-foreground mt-0.5">
        {fr ? 'Cible' : 'Target'} {target}{m.unit} · <span className={okColor}>{pct}%</span>
      </p>
    </div>
  );
};

/**
 * Iter. 406.fr.42 — MacroMiniChart.
 * Small-multiples approach: one chart per macro, with its OWN Y-axis scale
 * so protéines / glucides / lipides ne sont plus écrasés par les kcal.
 * Chaque graphe garde le style Glow (gradient + drop-shadow + ligne cible).
 */
const MacroMiniChart = ({ metricKey, points, target, fr }) => {
  const m = METRICS[metricKey];
  const Icon = m.icon;

  // Extract latest & max values for the header stat
  const values = points.map(p => Number(p[metricKey] || 0));
  const latest = values.length ? values[values.length - 1] : 0;
  const peak = values.length ? Math.max(...values) : 0;

  return (
    <div
      className="rounded-xl bg-card/70 ring-1 ring-border/60 p-3 hover:ring-orange-500/30 transition-all"
      style={{ boxShadow: `inset 0 0 24px ${m.glow.replace('0.55','0.08')}` }}
      data-testid={`macro-mini-${metricKey}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <span
            className="w-6 h-6 rounded-md flex items-center justify-center"
            style={{ background: m.glow.replace('0.55','0.15') }}
          >
            <Icon className="w-3.5 h-3.5" style={{ color: m.color }} />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-widest text-foreground">
            {fr ? m.label_fr : m.label_en}
          </span>
        </div>
        <div className="text-right leading-tight">
          <p
            className="font-heading text-base tabular-nums leading-none"
            style={{ color: m.color, textShadow: `0 0 8px ${m.glow}` }}
          >
            {Math.round(latest)}
            <span className="text-[9px] text-muted-foreground ml-0.5">{m.unit}</span>
          </p>
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">
            {fr ? 'pic' : 'peak'} {Math.round(peak)}
          </p>
        </div>
      </div>

      <div className="h-32 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 6, right: 4, left: -22, bottom: 0 }}>
            <defs>
              <linearGradient id={`mini-glow-${metricKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={m.color} stopOpacity={0.55} />
                <stop offset="70%"  stopColor={m.color} stopOpacity={0.15} />
                <stop offset="100%" stopColor={m.color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="label"
              tick={{ fill: '#71717a', fontSize: 9 }}
              axisLine={{ stroke: '#3f3f46' }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={16}
            />
            <YAxis
              tick={{ fill: '#71717a', fontSize: 9 }}
              axisLine={false}
              tickLine={false}
              width={30}
              domain={[0, (dataMax) => Math.max(dataMax * 1.15, target * 1.1 || 0)]}
            />
            <Tooltip
              cursor={{ stroke: m.color, strokeOpacity: 0.4 }}
              contentStyle={{
                background: '#09090b',
                border: `1px solid ${m.color}55`,
                borderRadius: 8,
                fontSize: 11,
                padding: '6px 10px',
              }}
              labelStyle={{ color: '#e2e8f0', fontSize: 10 }}
              formatter={(val) => [
                `${Math.round(val)} ${m.unit}`,
                fr ? m.label_fr : m.label_en,
              ]}
            />
            {target > 0 && (
              <ReferenceLine
                y={target}
                stroke={m.color}
                strokeDasharray="4 3"
                strokeOpacity={0.55}
                label={{
                  value: `${fr ? 'cible' : 'target'} ${target}`,
                  position: 'insideTopRight',
                  fill: m.color,
                  fontSize: 9,
                  opacity: 0.85,
                }}
              />
            )}
            <Area
              type="monotone"
              dataKey={metricKey}
              stroke={m.color}
              strokeWidth={2}
              fill={`url(#mini-glow-${metricKey})`}
              fillOpacity={1}
              activeDot={{
                r: 4,
                fill: m.color,
                stroke: '#0a0a0a',
                strokeWidth: 1.5,
                style: { filter: `drop-shadow(0 0 6px ${m.glow})` },
              }}
              style={{ filter: `drop-shadow(0 0 6px ${m.glow.replace('0.55','0.3')})` }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/**
 * Iter. 406.fr.43 — MacrosOverlayChart.
 * Normalized overlay view: each macro plotted as `% of its target` so all
 * four series fit on a shared 0–150% axis. Perfect for spotting balance
 * (all four curves close to 100 = well aligned).
 */
const MacrosOverlayChart = ({ points, targets, fr }) => {
  // Transform points → normalized % values.
  const normPoints = points.map(p => {
    const out = { label: p.label, date: p.date };
    Object.keys(METRICS).forEach((k) => {
      const target = targets[k];
      const raw = Number(p[k] || 0);
      out[k] = target > 0 ? Math.round((raw / target) * 100) : 0;
      // Keep raw for tooltip
      out[`${k}_raw`] = raw;
    });
    return out;
  });

  const zones = [
    { color: '#10b981', label: fr ? 'Cible 90-110%' : 'Target 90-110%' },
    { color: '#f59e0b', label: fr ? 'Sous/Excès 70-130%' : 'Under/Over 70-130%' },
    { color: '#ef4444', label: fr ? 'Hors cible' : 'Off-target' },
  ];

  return (
    <>
      <div className="h-72 w-full" data-testid="macros-overlay-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={normPoints} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
          <defs>
            {Object.entries(METRICS).map(([key, m]) => (
              <linearGradient key={key} id={`overlay-glow-${key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={m.color} stopOpacity={0.35} />
                <stop offset="70%"  stopColor={m.color} stopOpacity={0.08} />
                <stop offset="100%" stopColor={m.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <XAxis
            dataKey="label"
            tick={{ fill: '#71717a', fontSize: 10 }}
            axisLine={{ stroke: '#3f3f46' }}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fill: '#71717a', fontSize: 10 }}
            axisLine={{ stroke: '#3f3f46' }}
            tickLine={false}
            width={38}
            domain={[0, 150]}
            ticks={[0, 50, 100, 150]}
            tickFormatter={(v) => `${v}%`}
          />

          {/* Iter. 406.fr.44 — Balance zones (drawn BEFORE areas so they sit behind).
              🟢 90-110%  = zone cible
              🟠 70-90%   & 110-130% = sous-optimal / excès léger
              🔴 <70%     & >130%    = hors cible */}
          <ReferenceArea y1={0}   y2={70}  fill="#ef4444" fillOpacity={0.05} strokeOpacity={0} ifOverflow="hidden" />
          <ReferenceArea y1={70}  y2={90}  fill="#f59e0b" fillOpacity={0.06} strokeOpacity={0} ifOverflow="hidden" />
          <ReferenceArea y1={90}  y2={110} fill="#10b981" fillOpacity={0.09} strokeOpacity={0} ifOverflow="hidden" />
          <ReferenceArea y1={110} y2={130} fill="#f59e0b" fillOpacity={0.06} strokeOpacity={0} ifOverflow="hidden" />
          <ReferenceArea y1={130} y2={150} fill="#ef4444" fillOpacity={0.05} strokeOpacity={0} ifOverflow="hidden" />
          <Tooltip
            cursor={{ stroke: '#f97316', strokeOpacity: 0.4 }}
            contentStyle={{
              background: '#09090b',
              border: '1px solid rgba(249,115,22,0.35)',
              borderRadius: 8,
              fontSize: 11,
              padding: '6px 10px',
            }}
            labelStyle={{ color: '#e2e8f0', fontSize: 10, marginBottom: 4 }}
            formatter={(value, key, entry) => {
              const m = METRICS[key];
              if (!m) return null;
              const raw = entry?.payload?.[`${key}_raw`] ?? 0;
              return [
                `${value}% (${Math.round(raw)} ${m.unit})`,
                fr ? m.label_fr : m.label_en,
              ];
            }}
          />
          {/* Target line at 100% */}
          <ReferenceLine
            y={100}
            stroke="#f97316"
            strokeDasharray="4 3"
            strokeOpacity={0.55}
            label={{
              value: fr ? 'Cible 100%' : 'Target 100%',
              position: 'insideTopRight',
              fill: '#fdba74',
              fontSize: 10,
            }}
          />
          {['fats_g', 'carbs_g', 'protein_g', 'calories'].map((key) => {
            const m = METRICS[key];
            return (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={m.color}
                strokeWidth={2}
                fill={`url(#overlay-glow-${key})`}
                fillOpacity={1}
                activeDot={{
                  r: 4,
                  fill: m.color,
                  stroke: '#0a0a0a',
                  strokeWidth: 1.5,
                  style: { filter: `drop-shadow(0 0 6px ${m.glow})` },
                }}
                style={{ filter: `drop-shadow(0 0 4px ${m.glow.replace('0.55','0.25')})` }}
                isAnimationActive={false}
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>
    </div>

    {/* Zone legend */}
    <div
      className="mt-2 flex flex-wrap items-center justify-center gap-3"
      data-testid="macros-overlay-zone-legend"
    >
      {zones.map((z) => (
        <span
          key={z.label}
          className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground"
        >
          <span
            className="w-2.5 h-2.5 rounded-sm"
            style={{ background: z.color, opacity: 0.35 }}
          />
          {z.label}
        </span>
      ))}
    </div>
    </>
  );
};


/**
 * Iter. 406.fr.44 — Balance Score card (Strava-lite unified KPI).
 * Score 0-100 = % of logged days where ALL 4 macros were in the green
 * 90-110% target zone. Radial ring + per-macro hit bars.
 */
const BalanceScoreCard = ({ balance, fr }) => {
  const score = balance.score || 0;
  const macros = balance.per_macro_hit_pct || {};
  const macroKeys = ['calories', 'protein_g', 'carbs_g', 'fats_g'];
  const weakest = balance.weakest_macro;

  // Ring geometry
  const R = 42;
  const C = 2 * Math.PI * R;
  const offset = C - (score / 100) * C;

  const scoreColor = score >= 70 ? '#10b981' : score >= 40 ? '#f59e0b' : '#ef4444';
  const scoreLabel = fr
    ? (score >= 70 ? 'Excellent' : score >= 40 ? 'À affiner' : 'À rebâtir')
    : (score >= 70 ? 'Excellent' : score >= 40 ? 'Refine' : 'Rebuild');
  const weakestLabel = weakest && METRICS[weakest]
    ? (fr ? METRICS[weakest].label_fr : METRICS[weakest].label_en)
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-5 rounded-2xl bg-gradient-to-br from-card/80 via-card/60 to-emerald-950/20 ring-1 ring-emerald-500/30 p-4"
      style={{ boxShadow: `0 0 22px ${scoreColor}22, inset 0 0 24px rgba(16,185,129,0.05)` }}
      data-testid="balance-score-card"
    >
      <div className="flex items-center gap-4">
        {/* Radial ring */}
        <div className="relative shrink-0 w-24 h-24" data-testid="balance-score-ring">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r={R}
              stroke="rgba(148,163,184,0.15)" strokeWidth="6" fill="none" />
            <circle cx="50" cy="50" r={R}
              stroke={scoreColor} strokeWidth="6" fill="none"
              strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={offset}
              style={{
                transition: 'stroke-dashoffset 0.8s ease-out',
                filter: `drop-shadow(0 0 6px ${scoreColor})`,
              }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
            <span
              className="font-heading font-black text-2xl tabular-nums"
              style={{ color: scoreColor, textShadow: `0 0 10px ${scoreColor}88` }}
              data-testid="balance-score-value"
            >
              {score}
            </span>
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">
              /100
            </span>
          </div>
        </div>

        {/* Right side: label + bars */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 mb-2">
            <div>
              <p
                className="font-heading text-sm font-bold uppercase tracking-widest"
                style={{ color: scoreColor }}
              >
                {fr ? 'Score d\'Équilibre' : 'Balance Score'}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {balance.balanced_days}/{balance.days_evaluated} {fr ? 'jours dans la zone cible' : 'days in target zone'} · <span style={{ color: scoreColor }}>{scoreLabel}</span>
              </p>
            </div>
          </div>

          {/* Per-macro hit bars */}
          <div className="space-y-1" data-testid="balance-macro-bars">
            {macroKeys.map((k) => {
              const m = METRICS[k];
              const pct = macros[k] || 0;
              const isWeakest = k === weakest;
              return (
                <div key={k} className="flex items-center gap-2" data-testid={`balance-macro-${k}`}>
                  <span className="text-[10px] font-semibold w-14 shrink-0" style={{ color: m.color }}>
                    {fr ? m.label_fr : m.label_en}
                  </span>
                  <div className="flex-1 h-1.5 rounded-full bg-secondary/50 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${pct}%`,
                        background: m.color,
                        boxShadow: `0 0 6px ${m.glow}`,
                      }}
                    />
                  </div>
                  <span className={`text-[10px] tabular-nums w-9 text-right shrink-0 ${isWeakest ? 'text-orange-300 font-bold' : 'text-muted-foreground'}`}>
                    {pct}%
                  </span>
                </div>
              );
            })}
          </div>

          {weakestLabel && score < 100 && (
            <p className="text-[10px] text-orange-300/80 mt-2">
              {fr ? '→ Axe le plus faible :' : '→ Weakest axis:'} <span className="font-bold">{weakestLabel}</span>
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default function MacrosInsights() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const fr = (language || 'fr').startsWith('fr');

  const [period, setPeriod] = useState('week');
  const [chartView, setChartView] = useState('split');   // 'split' | 'overlay'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter. 406.fr.41 — lazy-loaded AI narrative (fetched after chart mounts).
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/macros/insights?period=${period}`);
      setData(res.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [period]);

  // Fetch AI summary IN PARALLEL, non-blocking for the chart
  const fetchAiSummary = useCallback(async () => {
    setAiLoading(true);
    setAiSummary(null);
    try {
      const res = await api.get(`/macros/insights/ai-summary?period=${period}`);
      setAiSummary(res.data);
    } catch { setAiSummary({ summary: null, source: 'ai_failed' }); }
    finally { setAiLoading(false); }
  }, [period]);

  useEffect(() => { fetchData(); fetchAiSummary(); }, [fetchData, fetchAiSummary]);

  const points = data?.points || [];
  const targets = data?.targets || {};
  const averages = data?.averages || {};
  const balance = data?.balance || null;

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12 max-w-5xl mx-auto" data-testid="macros-insights-page">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-secondary/60 flex items-center justify-center hover:bg-secondary transition-colors"
            data-testid="macros-insights-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.25em] text-orange-400 font-bold flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              {fr ? 'Analyse Macros' : 'Macros Analytics'}
            </p>
            <h1 className="font-heading text-2xl uppercase tracking-tight text-foreground">
              {fr ? 'Consommation' : 'Consumption'}
            </h1>
          </div>
        </div>

        {/* Period tabs */}
        <div className="mb-5 flex gap-1.5 p-1 rounded-xl bg-secondary/40 ring-1 ring-border/60 w-fit" data-testid="macros-period-tabs">
          {PERIOD_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setPeriod(t.key)}
              className={`px-4 py-1.5 text-xs font-black uppercase tracking-wider rounded-lg transition-all ${
                period === t.key
                  ? 'bg-orange-500/20 text-orange-200 ring-1 ring-orange-500/50 shadow-[0_0_10px_rgba(249,115,22,0.35)]'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`macros-period-${t.key}`}
            >
              {fr ? t.fr : t.en}
            </button>
          ))}
        </div>

        {/* Iter. 406.fr.41 — AI insight card (lazy-loaded, non-blocking) */}
        {(aiLoading || (aiSummary && aiSummary.summary)) && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-5"
          >
            <div
              className="p-4 rounded-xl bg-gradient-to-br from-orange-500/8 via-purple-500/6 to-cyan-500/8 ring-1 ring-orange-500/25 relative overflow-hidden"
              style={{ boxShadow: '0 0 18px rgba(249,115,22,0.15), inset 0 0 40px rgba(168,85,247,0.05)' }}
              data-testid="macros-ai-insight-card"
            >
              <div className="flex items-start gap-2.5">
                <span className="mt-0.5 w-7 h-7 rounded-lg bg-orange-500/15 ring-1 ring-orange-500/30 flex items-center justify-center shrink-0">
                  <Brain className="w-3.5 h-3.5 text-orange-300" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-300 mb-1 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" />
                    {fr ? "Lecture Coach YUMIX" : "Coach YUMIX read"}
                  </p>
                  {aiLoading ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      {fr ? "Le coach analyse ta courbe…" : 'The coach is analyzing your curve…'}
                    </div>
                  ) : (
                    <p className="text-sm text-foreground leading-relaxed" data-testid="macros-ai-insight-text">
                      {aiSummary.summary}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Averages 4-card grid */}
        {!loading && data && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-5"
          >
            <MacroCard metricKey="calories"  avg={averages.calories}  target={targets.calories}  fr={fr} />
            <MacroCard metricKey="protein_g" avg={averages.protein_g} target={targets.protein_g} fr={fr} />
            <MacroCard metricKey="carbs_g"   avg={averages.carbs_g}   target={targets.carbs_g}   fr={fr} />
            <MacroCard metricKey="fats_g"    avg={averages.fats_g}    target={targets.fats_g}    fr={fr} />
          </motion.div>
        )}

        {/* Iter. 406.fr.44 — Balance Score card (Strava-lite unified KPI) */}
        {!loading && data && balance && balance.days_evaluated > 0 && (
          <BalanceScoreCard balance={balance} fr={fr} />
        )}

        {/* Chart card */}
        <Card className="bg-card/80 border-border/60 overflow-hidden">
          <CardContent className="p-3 md:p-5">
            {loading ? (
              <div className="h-72 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-orange-400" />
              </div>
            ) : points.length === 0 ? (
              <div className="h-72 flex flex-col items-center justify-center text-center px-6">
                <p className="text-sm text-muted-foreground">
                  {fr ? 'Aucune donnée sur cette période.' : 'No data for this period.'}
                </p>
              </div>
            ) : (
              <>
                {/* Iter. 406.fr.43 — View toggle (Split / Overlay) */}
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                    {fr ? 'Consommation quotidienne' : 'Daily consumption'} · {points.length} {fr ? 'points' : 'points'}
                    {averages.days_logged != null && (
                      <span className="ml-2 text-emerald-300">
                        ({averages.days_logged}/{averages.days_in_window} {fr ? 'jours loggés' : 'days logged'})
                      </span>
                    )}
                  </p>
                  <div
                    className="flex items-center gap-0.5 bg-secondary/50 rounded-lg p-0.5"
                    data-testid="macros-view-toggle"
                  >
                    <button
                      onClick={() => setChartView('split')}
                      className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest rounded-md transition-all ${
                        chartView === 'split'
                          ? 'bg-orange-500/20 text-orange-300 shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid="macros-view-split"
                    >
                      {fr ? 'Séparé' : 'Split'}
                    </button>
                    <button
                      onClick={() => setChartView('overlay')}
                      className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest rounded-md transition-all ${
                        chartView === 'overlay'
                          ? 'bg-orange-500/20 text-orange-300 shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid="macros-view-overlay"
                    >
                      {fr ? 'Superposé' : 'Overlay'}
                    </button>
                  </div>
                </div>

                {chartView === 'split' ? (
                  /* Small multiples : chaque macro son propre graphique */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="macros-glow-grid">
                    {['calories', 'protein_g', 'carbs_g', 'fats_g'].map((key) => (
                      <MacroMiniChart
                        key={key}
                        metricKey={key}
                        points={points}
                        target={targets[key]}
                        fr={fr}
                      />
                    ))}
                  </div>
                ) : (
                  /* Overlay normalisé : 4 macros en % de leur cible sur le même axe */
                  <MacrosOverlayChart
                    points={points}
                    targets={targets}
                    fr={fr}
                  />
                )}

                {/* Legend */}
                <div className="mt-4 flex flex-wrap justify-center gap-3">
                  {Object.entries(METRICS).map(([key, m]) => (
                    <span key={key} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color, boxShadow: `0 0 6px ${m.glow}` }} />
                      {fr ? m.label_fr : m.label_en}
                    </span>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Contextual insight */}
        {!loading && averages.days_logged === 0 && (
          <p className="mt-4 text-center text-xs text-muted-foreground">
            {fr
              ? "Commence à logger tes repas via l'onglet Repas pour voir apparaître ta courbe personnelle."
              : 'Start logging meals via the Meals tab to see your personal curve build up.'}
          </p>
        )}
      </div>
    </Layout>
  );
}
